/**
 * Copies a text to the clipboard, reporting honestly whether it worked.
 *
 * Tries the asynchronous clipboard first; if that is refused (no permission, an insecure page, an in-app browser) it falls back to
 * the older command on a TEMPORARY, off-screen field that is removed at once - so the text is never left in the page, and nothing
 * visible has to be selected (a secret on screen stays a plain element nobody can accidentally drag-select).
 *
 * Returns `false` when neither worked: the caller must then say so and let the person write the text down.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return legacyCopy(text);
  }
}

function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.setAttribute('aria-hidden', 'true');
  field.style.position = 'fixed';
  field.style.insetBlockStart = '-1000px';
  field.style.opacity = '0';
  document.body.appendChild(field);
  try {
    field.select();
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    field.value = '';
    field.remove();
  }
}
